import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/next";
import type { AdminDashUserResponse } from "@packages/sdk";
import { passwordAuthFlowSchema } from "@packages/sdk";
import {
  Avatar,
  Button,
  colors,
  createModal,
  cssMediaQueryMinWidthTablet,
  ListProfile,
  Modal,
  ModalHeader,
  PasswordVisibilityButton,
  spacing,
  standardSchemaResolver,
  Text,
  TextInput,
  type ThemeType,
  useModal,
  useValidator,
} from "@packages/ui";
import { useState } from "react";
import { useForm } from "react-hook-form";

import { europeanStyleName } from "../../../lib";

const modalStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  gap: spacing.l,
  justifyContent: "flex-start",
  maxWidth: 480,
  padding: spacing.xl,
  [cssMediaQueryMinWidthTablet]: {
    padding: spacing.xl,
  },
};
const formStyles: Interpolation = {
  display: "flex",
  flexDirection: "column",
  flexWrap: "nowrap",
  rowGap: spacing.m,
  width: spacing.full,
};
const inputStyles: Interpolation<ThemeType> = (theme) => ({
  background: colors.transparent,
  color: theme.semanticColors.primary,
  flexGrow: 1,
  outline: "none",
});
const placeholderStyles: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsMedHigh,
});
const reqdStyles: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.errorError,
  marginTop: spacing.negativeS,
});
const descStyles: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsMedium,
  marginTop: spacing.negativeS,
});
const disabledProfileOverrideStyle: Interpolation = {
  cursor: "default",
  opacity: 1,
  background: "transparent",
  ":hover": {
    background: "transparent",
  },
};
const headerStyle: Interpolation = {
  justifyContent: "flex-start",
  margin: spacing.none,
  padding: spacing.none,
  textAlign: "left",
  width: spacing.full,
};
const h2Style: Interpolation = {
  justifyContent: "flex-start",
  margin: spacing.none,
  width: spacing.full,
};

type FormDataProps = {
  password: string;
};

export const ResetPasswordModal = createModal(
  ({ user }: { user: AdminDashUserResponse }) => {
    const modal = useModal();
    const { resolver, errors, errorsCSS, inputProps, showError } =
      useValidator<FormDataProps>(
        standardSchemaResolver(passwordAuthFlowSchema),
      );
    const {
      control,
      handleSubmit,
      formState: { isValid, isSubmitting },
      getValues,
    } = useForm<FormDataProps>({ resolver });
    const [visible, setVisible] = useState<boolean>(false);
    const t = useTranslations();

    const handleForm = (values: FormDataProps) => {
      modal.resolve(values);
      modal.remove();
    };

    const userEmail = user.email as string;

    return (
      <Modal
        contentCSS={modalStyle}
        open={modal.visible}
        onOpenChange={(open) => {
          if (open) return modal.show();
          modal.resolve();
          modal.remove();
        }}
        showClose
      >
        <ModalHeader
          type="headline"
          size="s"
          css={headerStyle}
          titleCSS={h2Style}
        >
          {t("reset_pw_title")}
        </ModalHeader>
        <div>
          {t(
            !!userEmail?.length
              ? "web_admin_dash_password_modal_description_email"
              : "web_admin_dash_password_modal_description_username",
          )}
        </div>
        <ListProfile
          avatar={<Avatar imgSrc={user.image_url} name={user.name} size={64} />}
          title={europeanStyleName(
            user as { name: string | null; last_name: string | null },
          )}
          metadata={
            !!userEmail?.length ? userEmail : `@${String(user.username)}`
          }
          icon={null}
          disabled
          css={disabledProfileOverrideStyle}
        />

        <form onSubmit={handleSubmit(handleForm)} css={formStyles}>
          <TextInput.Field
            control={control}
            name="password"
            required
            autoCorrect="off"
            autoComplete="new-password"
            type={visible ? "text" : "password"}
            placeholder={t("change_password_new_placeholder")}
            {...inputProps}
            css={inputStyles}
            placeholderCSS={[placeholderStyles]}
            button={
              <PasswordVisibilityButton
                visible={visible}
                onClick={() => setVisible(!visible)}
              />
            }
          />
          {showError?.password && !!errors?.password ? (
            <Text
              as="p"
              type="detail"
              size="l"
              css={[reqdStyles, errorsCSS.password]}
            >
              {getValues().password?.length === 0
                ? t("input_validation_error_required")
                : (t(errors.password.message) ?? "")}
            </Text>
          ) : (
            <Text as="p" type="detail" size="l" css={descStyles}>
              {t("web_admin_dash_password_field_instruction")}
            </Text>
          )}
          <Button
            type="submit"
            disabled={!isValid || isSubmitting}
            size="l"
            isFullWidth
          >
            {t("reset_pw_title")}
          </Button>
        </form>
      </Modal>
    );
  },
);
